import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { PaperProps } from '@mui/material/Paper';
import { snakeCase } from 'lodash';
import { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import isScrollable from '../helpers/isScrollable';
import DialogHeader from './DialogHeader';

type NoCloseReason = 'escapeKeyDown' | 'backdropClick';
type HeaderVariant = 'primary' | 'danger';

interface IModalProps extends Omit<DialogProps, 'onClose' | 'open' | 'title' | 'PaperProps'> {
  actions?: ReactNode;
  canClose?: boolean;
  children?: ReactNode;
  headerVariant?: HeaderVariant;
  name: string;
  noCloseReasons?: NoCloseReason[];
  onClose: () => void;
  open?: boolean;
  PaperProps?: Partial<PaperProps>;
  title?: string | ReactElement;
}

const styles = {
  actions: {
    '& button': {
      ml: '10px',
    },
    pb: 3,
    pt: 2,
    px: 3,
  },
  actionsBorderTop: {
    '&.MuiDialogActions-root': {
      borderTop: '1px solid',
    },
  },
  content: {
    '&.MuiDialogContent-root': {
      pb: 3,
      pt: 2.25,
      px: 3,
    },
  },
};

export function Modal({
  actions,
  canClose,
  children,
  fullWidth,
  headerVariant,
  maxWidth,
  name,
  noCloseReasons = [],
  onClose,
  open = false,
  PaperProps,
  title,
  ...rest
}: IModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);

  const [isScrollableState, setIsScrollableState] = useState(false);

  useEffect(() => {
    if (!dialogContentRef.current || !contentRef.current || !open) return;
    const dialogHeight = dialogContentRef.current.getBoundingClientRect().height;
    const contentHeight = contentRef.current.getBoundingClientRect().height;
    const scrollable = isScrollable(dialogHeight, contentHeight);
    setIsScrollableState(scrollable);
  }, [open]);

  const getHtmlId = (section: string) => `${snakeCase(name)}-${snakeCase(section)}`;

  const onReallyClose = () => {
    if (canClose ?? true) {
      onClose();
    }
  };

  return (
    <Dialog
      aria-describedby="alert-dialog-slide-description"
      aria-labelledby="alert-dialog-slide-title"
      fullWidth={fullWidth || true}
      keepMounted
      maxWidth={maxWidth || 'sm'}
      onClose={(_, reason: NoCloseReason) => {
        if (!noCloseReasons.includes(reason)) {
          onReallyClose();
        }
      }}
      open={open}
      slotProps={{ paper: { component: 'div', ...PaperProps } }}
      scroll="paper"
      {...rest}
    >
      {title && (
        <DialogHeader
          canClose={canClose}
          headerVariant={headerVariant}
          id={getHtmlId('title')}
          onCancel={onClose}
          title={title}
        />
      )}
      <DialogContent id={getHtmlId('content')} ref={dialogContentRef} sx={styles.content}>
        <div ref={contentRef}>{children}</div>
      </DialogContent>
      {actions && (
        <DialogActions
          id={getHtmlId('actions')}
          sx={{
            ...styles.actions,
            ...(isScrollableState && styles.actionsBorderTop),
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}
